import { Check } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Brand } from '../components/Brand'
import { listOfferedPlans, myActiveSubscription, startCheckout } from '../lib/pocketbase'
import type { Plan } from '../types'

export function SubscribePage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const status = searchParams.get('status')
  const [plans, setPlans] = useState<Plan[] | null>(null)
  const [busyPlanId, setBusyPlanId] = useState<string | null>(null)
  const [error, setError] = useState('')
  const [confirming, setConfirming] = useState(status === 'success')

  useEffect(() => { listOfferedPlans().then(setPlans).catch(() => setPlans([])) }, [])

  useEffect(() => {
    if (status !== 'success') return
    let cancelled = false
    void (async () => {
      for (let attempt = 0; attempt < 10 && !cancelled; attempt += 1) {
        const subscription = await myActiveSubscription()
        if (subscription) { navigate('/hoje', { replace: true }); return }
        await new Promise((resolve) => window.setTimeout(resolve, 1500))
      }
      if (!cancelled) setConfirming(false)
    })()
    return () => { cancelled = true }
  }, [status, navigate])

  const subscribe = async (plan: Plan) => {
    setBusyPlanId(plan.id); setError('')
    try {
      const { url } = await startCheckout(plan.id)
      window.location.href = url
    } catch {
      setError('Não foi possível iniciar o pagamento. Tente de novo em instantes.')
      setBusyPlanId(null)
    }
  }

  return <main className="subscribe-page">
    <header><Brand /></header>
    <div className="subscribe-card">
      <p className="step-label">Assinatura</p>
      <h1>Escolha como continuar sua escuta.</h1>
      <p>Uma obra por semana, sem prova — só acesso completo aos encontros e à comunidade de quem também está ouvindo.</p>

      {status === 'success' && confirming && <p className="subscribe-notice subscribe-notice--ok"><Check />Pagamento recebido! Estamos confirmando com a Stripe — isso leva só alguns segundos…</p>}
      {status === 'success' && !confirming && <p className="subscribe-notice">Ainda estamos confirmando seu pagamento. Se demorar mais que o esperado, atualize a página em instantes.</p>}
      {status === 'cancel' && <p className="subscribe-notice">Checkout cancelado. Pode tentar de novo quando quiser.</p>}
      {error && <p className="form-error">{error}</p>}

      {!plans ? <p>Carregando planos…</p> : plans.length === 0 ? (
        <p>Nenhum plano disponível no momento. Volte em breve.</p>
      ) : (
        <div className="subscribe-plans">
          {plans.map((plan) => (
            <div key={plan.id} className="plan-card">
              <h2>{plan.name}</h2>
              <p className="plan-card__price">{plan.price_label}</p>
              {plan.description && <p>{plan.description}</p>}
              {plan.features.length > 0 && <ul>
                {plan.features.map((feature) => <li key={feature}><Check size={16} />{feature}</li>)}
              </ul>}
              <button
                type="button" className="button button--primary"
                disabled={busyPlanId !== null || !plan.stripe_price_id}
                onClick={() => subscribe(plan)}
              >
                {busyPlanId === plan.id ? 'Abrindo checkout…' : 'Assinar'}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  </main>
}
